import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import { VisibilityOutlined as VisibilityOutlinedIcon } from "@mui/icons-material";
import { Box, Divider, Menu, MenuItem, Tooltip } from "@mui/material";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { AI_DOCS_STUCK_PROCESSING_MINUTES } from "@platform/feature-flags";
import React from "react";
import { Typography } from "@platform/design-system";

import {
  PDF_PREVIEW_ONLY_ERROR_MESSAGE,
  DEFAULT_STUCK_PROCESSING_MINUTES,
  PDF_RENAME_ONLY_ERROR_MESSAGE,
} from "../constants";
import { useDocumentActions } from "../hooks";
import { isPdf } from "../utils";

import type { JSONValue } from "@growthbook/growthbook-react";
import type { DocumentCardProps } from "../types";

const BORDER_COLOR = "#babfc7";

export type DocumentCardActionsModalProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentsById: Record<string, DocumentCardProps>;
  limitActions?: boolean;
};

type ActionItem = {
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  dividerAfter?: boolean;
  disabled: boolean;
  tooltipMessage?: string;
};

const MS_PER_MINUTE = 60_000;

const stuckAfterMinutesFrom = (configured: JSONValue): number =>
  typeof configured === "number" && configured > 0
    ? configured
    : DEFAULT_STUCK_PROCESSING_MINUTES;

const isStuckProcessing = (
  document: DocumentCardProps,
  stuckAfterMinutes: number,
): boolean =>
  document.processingSince != null &&
  Date.now() - Date.parse(document.processingSince) >
    stuckAfterMinutes * MS_PER_MINUTE;

export const DocumentCardActionsModal = ({
  anchorEl,
  open,
  onClose,
  documentId,
  documentsById,
  limitActions = false,
}: DocumentCardActionsModalProps): React.JSX.Element => {
  const stuckAfterMinutes = stuckAfterMinutesFrom(
    useFeatureValue<JSONValue>(
      AI_DOCS_STUCK_PROCESSING_MINUTES,
      DEFAULT_STUCK_PROCESSING_MINUTES,
    ),
  );
  const foundDocument = documentsById[documentId];
  const isDocumentProcessing =
    foundDocument?.status === "processing" &&
    !isStuckProcessing(foundDocument, stuckAfterMinutes);
  const isDocumentExtracted = foundDocument?.status === "extracted";
  const downloadUrl =
    typeof foundDocument?.metadata?.downloadUrl === "string"
      ? foundDocument.metadata.downloadUrl
      : undefined;
  const isViewablePdf = Boolean(isPdf(downloadUrl));

  const { actions } = useDocumentActions(
    new Set([documentId]),
    onClose,
    documentsById,
  );

  const menuActions: ActionItem[] = [
    {
      label: "View Document",
      icon: <VisibilityOutlinedIcon fontSize="small" />,
      onClick: actions.viewDocument,
      disabled: !isViewablePdf,
      tooltipMessage: !isViewablePdf
        ? PDF_PREVIEW_ONLY_ERROR_MESSAGE
        : undefined,
    },
    {
      label: "Download Document",
      icon: <FileDownloadOutlinedIcon fontSize="small" />,
      onClick: actions.downloadDocument,
      disabled: false,
    },
    {
      label: "Rename Document",
      icon: <DriveFileRenameOutlineOutlinedIcon fontSize="small" />,
      onClick: actions.renameDocument,
      disabled: !isViewablePdf,
      tooltipMessage: !isViewablePdf
        ? PDF_RENAME_ONLY_ERROR_MESSAGE
        : undefined,
    },
    {
      label: "Process with AI",
      icon: <AutoAwesomeIcon fontSize="small" />,
      onClick: actions.processWithAI,
      disabled: limitActions || isDocumentProcessing || isDocumentExtracted,
    },
    {
      label: "Remove File",
      icon: <RemoveCircleOutlineOutlinedIcon fontSize="small" />,
      onClick: actions.removeFile,
      disabled: isDocumentProcessing,
    },
  ].filter(Boolean) as ActionItem[];

  return (
    <Menu
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            border: 1,
            borderColor: BORDER_COLOR,
            ml: 1.5,
          },
        },
      }}
    >
      {menuActions.flatMap((action) => {
        const menuItem = (
          <MenuItem
            key={action.label}
            onClick={action.onClick}
            disabled={action.disabled}
            sx={{
              gap: 1,
              ...(action.tooltipMessage && {
                "&.Mui-disabled": { pointerEvents: "auto" },
              }),
            }}
          >
            <Box sx={{ color: "text.secondary", display: "flex" }}>
              {action.icon}
            </Box>
            <Typography variant="body-medium">{action.label}</Typography>
          </MenuItem>
        );

        const items: React.ReactNode[] = [];

        items.push(
          action.tooltipMessage ? (
            <Tooltip
              key={action.label}
              title={action.tooltipMessage}
              placement="right"
            >
              {menuItem}
            </Tooltip>
          ) : (
            menuItem
          ),
        );

        if (action.dividerAfter) {
          items.push(
            <Divider
              key={`${action.label}-divider`}
              sx={{
                "&.MuiDivider-root": {
                  marginTop: 0,
                  marginBottom: 0,
                },
              }}
            />,
          );
        }

        return items;
      })}
    </Menu>
  );
};
